<template>
  <div>
    <RevDrawer
      :has-back-button="filterGroupToDisplay !== DEFAULT_GROUP_TO_DISPLAY"
      name="MERCHANT.OPPORTUNITIES.FILTERS"
      :title="modalTitle"
      @back="handleBack"
    >
      <template #body>
        <RevList v-if="filterGroupToDisplay === DEFAULT_GROUP_TO_DISPLAY">
          <RevListItemInteractive @click="displayFilterGroup('market')">
            <template #label>{{ i18n(translations.market) }}</template>
            <template #suffix>
              <IconChevronRight />
            </template>
          </RevListItemInteractive>
          <RevListItemInteractive @click="displayFilterGroup('categoryId')">
            <template #label>{{ i18n(translations.categoryId) }}</template>
            <template #suffix>
              <IconChevronRight />
            </template>
          </RevListItemInteractive>
          <RevListItemInteractive @click="displayFilterGroup('sku')">
            <template #label>{{ i18n(translations.sku) }}</template>
            <template #suffix>
              <IconChevronRight />
            </template>
          </RevListItemInteractive>
          <RevListItemInteractive
            @click="displayFilterGroup('priceDifference')"
          >
            <template #label>{{ i18n(translations.priceDifference) }}</template>
            <template #suffix>
              <IconChevronRight />
            </template>
          </RevListItemInteractive>
        </RevList>

        <template v-if="filterGroupToDisplay !== DEFAULT_GROUP_TO_DISPLAY">
          <RevInputSelect
            v-if="filterGroupToDisplay === 'market'"
            id="market"
            v-model="values.market"
            :label="i18n(translations.market)"
            :options="marketOptions"
          />

          <RevInputSelect
            v-if="filterGroupToDisplay === 'categoryId'"
            id="categoryId"
            v-model="values.categoryId"
            :label="i18n(translations.categoryId)"
            :options="categoryOptions"
          />

          <RevInputText
            v-if="filterGroupToDisplay === 'sku'"
            id="sku"
            v-model="values.sku"
            :label="i18n(translations.sku)"
            type="text"
          />

          <RevInputText
            v-if="filterGroupToDisplay === 'priceDifference'"
            id="priceDifference"
            v-model="values.priceDifference"
            class="mb-16"
            :label="i18n(translations.priceDifference)"
            type="text"
          />
        </template>
      </template>

      <template #footer="{ close }">
        <RevButton
          class="mb-8"
          full-width="always"
          variant="primary"
          @click="handleSubmit(close)"
        >
          {{ i18n(translations.viewResults) }}
        </RevButton>
        <RevButton
          class="mb-24"
          full-width="always"
          variant="secondary"
          @click="handleReset(close)"
        >
          {{ i18n(translations.resetFilters) }}
        </RevButton>
      </template>

      <template #trigger="{ open }">
        <RevStickyBar>
          <div class="flex items-center justify-center">
            <RevButton
              :icon="IconEqualizer"
              size="small"
              variant="secondary"
              @click="open"
            >
              {{ i18n(translations.filter) }}

              <RevTag
                v-if="currentFiltersCount > 0"
                :label="currentFiltersCount.toString()"
                variant="secondary"
              />
            </RevButton>
          </div>
        </RevStickyBar>
      </template>
    </RevDrawer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { type Option, RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevList } from '@ds/components/List'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'
import { RevStickyBar } from '@ds/components/StickyBar'
import { RevTag } from '@ds/components/Tag'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import { IconEqualizer } from '@ds/icons/IconEqualizer'

import { DEFAULT_INITIAL_VALUES } from '../Filters.constants'
import translations from '../Filters.translations'
import type { FilterValues } from '../Filters.types'

const emit = defineEmits(['submit'])
const props = defineProps<{
  initialValues: FilterValues
  marketOptions: Option[]
  categoryOptions: Option[]
}>()

const DEFAULT_GROUP_TO_DISPLAY = ''

const i18n = useI18n()
const filterGroupToDisplay = ref(DEFAULT_GROUP_TO_DISPLAY)
const values = ref({ ...props.initialValues })

const currentFiltersCount = computed(
  () =>
    Object.keys(DEFAULT_INITIAL_VALUES).filter(
      (key) =>
        values.value[key as keyof FilterValues] !==
        DEFAULT_INITIAL_VALUES[key as keyof FilterValues],
    ).length,
)

const modalTitle = computed(() => {
  const titles = {
    market: i18n(translations.market),
    categoryId: i18n(translations.categoryId),
    sku: i18n(translations.sku),
    priceDifference: i18n(translations.priceDifference),
  } as Record<string, string>

  return titles[filterGroupToDisplay.value] || i18n(translations.filter)
})

function handleBack() {
  filterGroupToDisplay.value = DEFAULT_GROUP_TO_DISPLAY
}

function handleSubmit(closeModalFunction: () => void) {
  emit('submit', values.value)
  filterGroupToDisplay.value = DEFAULT_GROUP_TO_DISPLAY
  closeModalFunction()
}
function handleReset(closeModalFunction: () => void) {
  values.value = { ...DEFAULT_INITIAL_VALUES }
  emit('submit', values.value)
  filterGroupToDisplay.value = DEFAULT_GROUP_TO_DISPLAY
  closeModalFunction()
}

function displayFilterGroup(groupName: string) {
  filterGroupToDisplay.value = groupName
}
</script>
